import { toast } from 'sonner';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus } from 'lucide-react';

import { clientApi } from '@/config/axios';
import type { PregnancyOut } from '../types';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MaternalLabs } from '@/features/labs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { PregnancyImaging } from '@/features/imaging';
import { formatCalendarDate } from '@/lib/utils/date';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiError, useImagingApi, useImagingCapabilities } from '@/features/imaging';
import { useCreatePregnancy, usePregnancies, useUpdatePregnancy } from '../api/pregnancies-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PregnancyFormData {
  abortions: number;
  edd: string;
  edd_method: string;
  fetus_count: number;
  gravida: number;
  height_inches: string;
  living: number;
  lmp: string;
  pre_pregnancy_weight: string;
  preterm: number;
  problems: string;
  term: number;
  vbac: boolean;
}

interface PregnancySectionProps {
  patientId: string;
}

const EMPTY_FORM: PregnancyFormData = {
  edd: '',
  lmp: '',
  term: 0,
  living: 0,
  gravida: 0,
  preterm: 0,
  vbac: false,
  abortions: 0,
  problems: '',
  edd_method: '',
  fetus_count: 1,
  height_inches: '',
  pre_pregnancy_weight: '',
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  loss: 'destructive',
  terminated: 'outline',
  delivered: 'secondary',
  ectopic: 'destructive',
};

interface PregnancyFormProps {
  form: PregnancyFormData;
  isEdit: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  setForm: Dispatch<SetStateAction<PregnancyFormData>>;
}

export function PregnancySection({ patientId }: PregnancySectionProps) {
  const { isLoading, data: pregnanciesData } = usePregnancies(patientId);
  const createMutation = useCreatePregnancy(patientId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [form, setForm] = useState<PregnancyFormData>(EMPTY_FORM);

  const pregnancies = pregnanciesData?.data ?? [];
  const active = pregnancies.find((p) => p.status === 'active');
  const past = pregnancies.filter((p) => p.status !== 'active');

  const updateMutation = useUpdatePregnancy(editingId ?? undefined);

  function handleCreate() {
    setIsCreating(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleEdit(p: PregnancyOut) {
    setEditingId(p.id);
    setIsCreating(false);
    setForm(pregnancyToFormData(p));
  }

  function handleCancel() {
    setIsCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleSave() {
    const payload = formDataToPayload(form);

    if (isCreating) {
      createMutation.mutate(payload, {
        onError: () => toast.error('Failed to create pregnancy'),
        onSuccess: () => {
          toast.success('Pregnancy created');
          handleCancel();
        },
      });
    } else if (editingId) {
      updateMutation.mutate(payload, {
        onError: () => toast.error('Failed to update pregnancy'),
        onSuccess: () => {
          toast.success('Pregnancy updated');
          handleCancel();
        },
      });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const showForm = isCreating || editingId !== null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Pregnancy</CardTitle>
          {!active && !showForm && (
            <Button size="sm" variant="outline" onClick={handleCreate}>
              <Plus className="mr-1 h-4 w-4" />
              Create Pregnancy
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <details>
          <summary>All patient laboratory results · View flowsheet</summary>
          <MaternalLabs patientId={patientId} />
        </details>
        {/* Active pregnancy summary */}
        {active && !showForm && (
          <details open className="flex flex-col gap-3">
            <summary>Current pregnancy · {active.id.slice(0, 8)}</summary>
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[active.status] ?? 'outline'}>{active.status}</Badge>
                {active.edd && (
                  <span className="text-sm text-muted-foreground">EDD: {formatCalendarDate(active.edd)}</span>
                )}
                {active.gtpal_display && <span className="text-sm font-mono">{active.gtpal_display}</span>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleEdit(active)}>
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {active.fetus_count > 0 && <span>Fetus count: {active.fetus_count}</span>}
              {active.vbac && <Badge variant="outline">VBAC</Badge>}
              {active.problems && <span className="text-destructive">Problems: {active.problems}</span>}
            </div>
            <PregnancyImaging pregnancyId={active.id} />
          </details>
        )}

        {/* No active pregnancy and not creating */}
        {!active && !showForm && pregnancies.length === 0 && (
          <p className="text-sm text-muted-foreground">No pregnancy records.</p>
        )}

        {/* Inline form for create / edit */}
        {showForm && (
          <PregnancyForm
            form={form}
            setForm={setForm}
            isSaving={isSaving}
            isEdit={!!editingId}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {/* Past pregnancies */}
        {past.length > 0 && (
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPast(!showPast)}>
              {showPast ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Past Pregnancies ({past.length})
            </button>
            {showPast && (
              <div className="mt-2 space-y-2">
                {past.map((p) => (
                  <details key={p.id} className="rounded-md border p-3">
                    <summary>
                      Pregnancy {p.id.slice(0, 8)} · {p.status}
                    </summary>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={STATUS_VARIANT[p.status] ?? 'outline'}>{p.status}</Badge>
                      {p.edd && <span className="text-muted-foreground">EDD: {formatCalendarDate(p.edd)}</span>}
                      {p.gtpal_display && <span className="font-mono">{p.gtpal_display}</span>}
                    </div>
                    <PregnancyImaging pregnancyId={p.id} />
                  </details>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formDataToPayload(f: PregnancyFormData): Record<string, unknown> {
  return {
    term: f.term,
    vbac: f.vbac,
    living: f.living,
    edd: f.edd || null,
    lmp: f.lmp || null,
    gravida: f.gravida,
    preterm: f.preterm,
    abortions: f.abortions,
    fetus_count: f.fetus_count,
    problems: f.problems || null,
    edd_method: f.edd_method || null,
    height_inches: f.height_inches ? Number(f.height_inches) : null,
    pre_pregnancy_weight: f.pre_pregnancy_weight ? Number(f.pre_pregnancy_weight) : null,
  };
}

/* ─── Inline Form ───────────────────────────────────────────────── */

function PregnancyForm({ form, onSave, isEdit, setForm, onCancel, isSaving }: PregnancyFormProps) {
  const { base } = useImagingApi();
  const cap = useImagingCapabilities();
  const [proposedEdd, setProposedEdd] = useState('');
  async function calculateLmp() {
    try {
      const asOf = new Date().toISOString().slice(0, 10);
      const result = await clientApi.get(`${base}/dating-preview`, { params: { as_of: asOf, lmp: form.lmp } });
      setProposedEdd(result.data.proposed_edd);
    } catch (error) {
      toast.error(apiError(error));
    }
  }
  function update<K extends keyof PregnancyFormData>(key: K, value: PregnancyFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      {/* Dates row */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="preg-edd">EDD</Label>
          <Input id="preg-edd" type="date" value={form.edd} onChange={(e) => update('edd', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-lmp">LMP</Label>
          <Input id="preg-lmp" type="date" value={form.lmp} onChange={(e) => update('lmp', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-edd-method">EDD Method</Label>
          <Select value={form.edd_method} onValueChange={(v) => update('edd_method', v)}>
            <SelectTrigger id="preg-edd-method" className="w-full">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lmp">LMP</SelectItem>
              <SelectItem value="ultrasound">Ultrasound</SelectItem>
              <SelectItem value="ivf">IVF</SelectItem>
              <SelectItem value="clinical">Clinical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {cap.data?.permissions.includes('prepare_imaging') && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" disabled={!form.lmp || isSaving} onClick={() => void calculateLmp()}>
            Calculate EDD from LMP
          </Button>
          {proposedEdd && (
            <>
              <span>Proposed EDD: {proposedEdd}. Review dating basis before saving.</span>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  update('edd', proposedEdd);
                  update('edd_method', 'lmp');
                  setProposedEdd('');
                }}>
                Use proposed date in form
              </Button>
            </>
          )}
        </div>
      )}
      {/* Fetus count */}
      <div className="space-y-1.5">
        <Label htmlFor="preg-fetus-count">Fetus Count</Label>
        <Input
          id="preg-fetus-count"
          max={5}
          min={1}
          type="number"
          className="w-24"
          value={form.fetus_count}
          onChange={(e) => update('fetus_count', Math.max(1, Math.min(5, Number(e.target.value))))}
        />
      </div>

      {/* GTPAL row */}
      <div className="space-y-1.5">
        <Label>GTPAL</Label>
        <div className="flex gap-2">
          {(['gravida', 'term', 'preterm', 'abortions', 'living'] as const).map((field) => {
            const label = { term: 'T', living: 'L', gravida: 'G', preterm: 'P', abortions: 'A' }[field];
            return (
              <div key={field} className="flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-muted-foreground">{label}</span>
                <Input
                  min={0}
                  type="number"
                  value={form[field]}
                  className="w-14 text-center"
                  onChange={(e) => update(field, Math.max(0, Number(e.target.value)))}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Weight / Height row */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="preg-weight">Pre-pregnancy Weight (lbs)</Label>
          <Input
            id="preg-weight"
            min={0}
            type="number"
            value={form.pre_pregnancy_weight}
            onChange={(e) => update('pre_pregnancy_weight', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-height">Height (inches)</Label>
          <Input
            id="preg-height"
            min={0}
            type="number"
            value={form.height_inches}
            onChange={(e) => update('height_inches', e.target.value)}
          />
        </div>
      </div>

      {/* VBAC */}
      <div className="flex items-center gap-2">
        <Checkbox id="preg-vbac" checked={form.vbac} onCheckedChange={(checked) => update('vbac', checked === true)} />
        <Label htmlFor="preg-vbac" className="cursor-pointer">
          VBAC
        </Label>
      </div>

      {/* Problems */}
      <div className="space-y-1.5">
        <Label htmlFor="preg-problems">Problems / Notes</Label>
        <Textarea
          id="preg-problems"
          rows={3}
          value={form.problems}
          onChange={(e) => update('problems', e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="outline" disabled={isSaving} onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={isSaving} onClick={onSave}>
          {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
}

function pregnancyToFormData(p: PregnancyOut): PregnancyFormData {
  return {
    term: p.term,
    vbac: p.vbac,
    edd: p.edd ?? '',
    lmp: p.lmp ?? '',
    living: p.living,
    gravida: p.gravida,
    preterm: p.preterm,
    abortions: p.abortions,
    fetus_count: p.fetus_count,
    problems: p.problems ?? '',
    edd_method: p.edd_method ?? '',
    height_inches: p.height_inches != null ? String(p.height_inches) : '',
    pre_pregnancy_weight: p.pre_pregnancy_weight != null ? String(p.pre_pregnancy_weight) : '',
  };
}
